import { useState } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useBulkChangeEventAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeEventAction';
import { useBulkChangeTicketTypeRuleAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeTicketTypeRuleAction';
import { useBulkEditCommissionAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditCommissionAction/useBulkEditCommissionAction';
import { useBulkEditMarketplaceSeatingAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditMarketplaceSeating';
import { useBulkRemovePredelivery } from 'src/components/Listings/InventoryActionDropdown/components/BulkRemovePredeliveryAction';
import { useBulkReturnTicketAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkReturnTicket';
import { useBulkUpdateAutoFulfillAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateAutoFulfillAction';
import { useBulkUpdateBarcodes } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateBarcodesAction/useBulkUpdateBarcodes';
import { useBulkUpdateDeliveryType } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateDeliveryType';
import { useBulkUpdateInternalNotesAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateInternalNotesAction';
import { useBulkUpdateListingSettings } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateListingSettingsAction';
import { useBulkUpdateSeatTraits } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateSeatTraits';
import { useBulkWasteTicketsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkWasteTicketsAction';
import { useBulkUnwasteTicketsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkUnwasteTicketsAction';
import { useBulkDeleteSeatSavers } from 'src/components/Listings/InventoryActionDropdown/components/LaunchDeleteSeatSaversAction';
import { useExportInventoryAction } from 'src/components/Listings/InventoryActionDropdown/components/LaunchExportInventoryAction/useExportInventory';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ChevronDownIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { EventWithData, Feature, Permission } from 'src/WebApiController';

interface SettingsActionsProps {
  event: EventWithData['event'];

  isEventPage?: boolean;
}

export const SettingsActions = ({
  event,
  isEventPage,
}: SettingsActionsProps) => {
  const { areBulkActionsEnabled } = useBulkActionsState();
  const hasExportInventoryFeature = useUserHasFeature(Feature.ExportInventory);
  const hasInventoryExportPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_Export
  );
  const hasBulkReturnTicketFeature = useUserHasFeature(
    Feature.BulkReturnTicket
  );
  const [isLoading, setIsLoading] = useState(false);
  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState(event);

  const bulkEditGeneralSettings = useBulkUpdateListingSettings(
    'general',
    filterQueryWithEventIds,
    affectedVisibleListingIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkUpdateInternalNotes = useBulkUpdateInternalNotesAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkUpdateAutoFulfill = useBulkUpdateAutoFulfillAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkRemovePredelivery = useBulkRemovePredelivery(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkChangeEvent = useBulkChangeEventAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkDeleteSeatSavers = useBulkDeleteSeatSavers(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );
  const bulkEditDeliverType = useBulkUpdateDeliveryType(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );
  const bulkEditSeatTraits = useBulkUpdateSeatTraits(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkChangeTicketTypeRule = useBulkChangeTicketTypeRuleAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkEditMarketplaceSeating = useBulkEditMarketplaceSeatingAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkEditCommissionAction = useBulkEditCommissionAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );
  const bulkWasteTicketsAction = useBulkWasteTicketsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkUnwasteTicketsAction = useBulkUnwasteTicketsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkUpdateBarcodesAction = useBulkUpdateBarcodes(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const exportInventoryAction = useExportInventoryAction({
    textContentId: ContentId.ExportListings,
    textOnly: true,
    disabled: !areBulkActionsEnabled,
    shape: 'rect',
    variant: 'text',
    style: { color: vars.color.textStrong },
  });

  const bulkReturnTicketActon = useBulkReturnTicketAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  return (
    <>
      <PosDropdown
        key="event-settings-actions-dropdown"
        trigger={
          <Button variant="outline">
            <Content id={ContentId.Other} />
            <ChevronDownIcon
              size={vars.iconSize.xs}
              fill={IconsFill.currentColor}
            />
          </Button>
        }
        align="start"
        triggerProps={{ disabled: isLoading || !areBulkActionsEnabled }}
      >
        {bulkEditGeneralSettings.dropDown}
        {bulkUpdateInternalNotes.dropDown}
        {bulkUpdateAutoFulfill.dropDown}
        {bulkRemovePredelivery.dropDown}
        {bulkUpdateBarcodesAction.dropDown}
        {bulkChangeEvent.dropDown}
        {bulkDeleteSeatSavers.dropDown}
        {bulkEditDeliverType.dropDown}
        {bulkEditSeatTraits.dropDown}
        {bulkChangeTicketTypeRule.dropDown}
        {bulkEditMarketplaceSeating.dropDown}
        {bulkEditCommissionAction.dropDown}
        {hasExportInventoryFeature &&
          hasInventoryExportPermission &&
          isEventPage &&
          exportInventoryAction.dropdown}
        {bulkWasteTicketsAction.dropDown}
        {bulkUnwasteTicketsAction.dropDown}
        {hasBulkReturnTicketFeature && bulkReturnTicketActon.dropDown}
      </PosDropdown>
      {bulkChangeEvent.dialog}
    </>
  );
};
